// PDF renderers for the weekly billing module (Phase 4).
//
// Two outputs:
//   renderLabInvoicePdf({ invoice, lines, lab })   → Blob
//   renderPhlebPayslipPdf({ payslip, lines, phleb }) → Blob
//
// Pure functions — no Supabase, no React. The caller writes the Blob to
// storage and updates the row's pdf_url column.
//
// Layout intentionally mirrors the style guide in generate-billing-portal-plan-pdf.js
// (teal header, gray subdued body, alternating zebra rows, Net 14 footer) so the
// admin / lab / phleb see a consistent visual identity across documents.

import { jsPDF } from 'jspdf';

const PAGE_W = 210; // A4 portrait, mm
const LM = 20;      // left margin
const RM = 190;     // right margin

const TEAL    = [0, 128, 128];
const WHITE   = [255, 255, 255];
const GRAY30  = [30, 30, 30];
const GRAY60  = [60, 60, 60];
const GRAY100 = [100, 100, 100];
const GRAY220 = [220, 220, 220];
const ZEBRA   = [250, 250, 250];

function fmtMoney(n) {
  return Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function fmtDate(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return ''; }
}

function fmtShort(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' }); }
  catch { return ''; }
}

// Short, stable, human-readable doc number derived from the row's UUID.
// e.g. "INV-A1B2C3D4" or "PAY-A1B2C3D4". Stable across re-renders.
function shortNumber(prefix, uuid) {
  if (!uuid) return `${prefix}-PENDING`;
  return `${prefix}-${uuid.replace(/-/g, '').slice(0, 8).toUpperCase()}`;
}

// Teal header band — used by both documents so they look like a set.
function drawHeader(doc, title, subtitle) {
  doc.setFillColor(...TEAL);
  doc.rect(0, 0, PAGE_W, 32, 'F');
  doc.setTextColor(...WHITE);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('AtHomeBloodDraw.com', LM, 16);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(title, LM, 25);
  if (subtitle) {
    doc.setFontSize(10);
    doc.text(subtitle, RM, 25, { align: 'right' });
  }
  doc.setTextColor(0, 0, 0);
}

// Two-column meta block: a "FIELD" label in gray, value beneath in black.
// Returns the new y cursor.
function metaPair(doc, x, y, label, value, valueBold = true) {
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRAY100);
  doc.text(label, x, y);
  doc.setFontSize(10);
  doc.setFont('helvetica', valueBold ? 'bold' : 'normal');
  doc.setTextColor(...GRAY30);
  doc.text(value || '—', x, y + 5);
  return y + 11;
}

// Footer band — page number + remit-to info, called once per page after content.
function drawFooter(doc, text) {
  const total = doc.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...GRAY100);
    doc.text(text, 105, 287, { align: 'center' });
    doc.text(`Page ${i} of ${total}`, RM, 287, { align: 'right' });
  }
}

// ── LAB INVOICE ─────────────────────────────────────────────────────────────

export function renderLabInvoicePdf({ invoice, lines, lab }) {
  const doc = new jsPDF();
  const weekLabel = `Week ending ${fmtDate(invoice.week_ending)}`;
  drawHeader(doc, 'Weekly Invoice', weekLabel);

  // ── Bill-to + meta side by side ────────────────────────────────────────
  let y = 45;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRAY100);
  doc.text('BILL TO', LM, y);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...GRAY30);
  doc.text(lab?.name || 'Unknown lab', LM, y + 6);
  if (lab?.address) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(lab.address, LM, y + 12);
  }
  const labEmail = lab?.billing_email || lab?.contact_email;
  if (labEmail) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...GRAY60);
    doc.text(labEmail, LM, y + 18);
  }

  // Right-side meta — invoice #, issue, due
  const dueDate = new Date(invoice.generated_at || Date.now());
  dueDate.setDate(dueDate.getDate() + 14);
  let mY = y;
  mY = metaPair(doc, 130, mY, 'INVOICE',    invoice.invoice_number || shortNumber('INV', invoice.id));
  mY = metaPair(doc, 130, mY, 'ISSUE DATE', fmtDate(invoice.generated_at || new Date()));
  mY = metaPair(doc, 130, mY, 'DUE DATE',   fmtDate(dueDate));

  // ── Line items header ──────────────────────────────────────────────────
  y = Math.max(y + 28, mY + 6);
  doc.setFillColor(...TEAL);
  doc.rect(LM, y, RM - LM, 7, 'F');
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...WHITE);
  doc.text('DATE',    LM + 2,  y + 5);
  doc.text('PATIENT', LM + 18, y + 5);
  doc.text('PHLEB',   LM + 72, y + 5);
  doc.text('DRAW',    155,     y + 5, { align: 'right' });
  doc.text('ADMIN',   172,     y + 5, { align: 'right' });
  doc.text('TOTAL',   188,     y + 5, { align: 'right' });
  // Header band is 7mm tall; advance enough for the 9pt body font ascender
  // (~3.2mm) plus a 3mm gap so the first row doesn't overlap the band edge.
  y += 13;

  // ── Line items rows ───────────────────────────────────────────────────
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRAY30);
  lines.forEach((l, i) => {
    if (y > 255) {
      doc.addPage();
      y = 25;
    }
    if (i % 2 === 1) {
      doc.setFillColor(...ZEBRA);
      doc.rect(LM, y - 4, RM - LM, 6.5, 'F');
    }
    doc.setFontSize(9);
    doc.text(fmtShort(l.draw_date),             LM + 2,  y);
    doc.text((l.patient_name || '—').slice(0, 32), LM + 18, y);
    doc.text((l.phleb_name || '—').slice(0, 26),   LM + 72, y);
    doc.text(fmtMoney(l.draw_fee),              155, y, { align: 'right' });
    doc.text(fmtMoney(l.admin_fee),             172, y, { align: 'right' });
    doc.text(fmtMoney(l.line_total),            188, y, { align: 'right' });
    y += 6.5;
  });

  // ── Totals box ─────────────────────────────────────────────────────────
  y += 4;
  if (y > 250) { doc.addPage(); y = 25; }
  doc.setDrawColor(...GRAY220);
  doc.setLineWidth(0.3);
  doc.line(120, y, RM, y);
  y += 6;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRAY60);
  doc.text('Subtotal (draws)', 122, y);
  doc.text(fmtMoney(invoice.subtotal_draws), 188, y, { align: 'right' });
  y += 6;
  doc.text('Admin fees', 122, y);
  doc.text(fmtMoney(invoice.admin_fee_total), 188, y, { align: 'right' });
  if (Number(invoice.ship_fee_total || 0) > 0) {
    y += 6;
    doc.text('Shipping', 122, y);
    doc.text(fmtMoney(invoice.ship_fee_total), 188, y, { align: 'right' });
  }
  y += 8;
  doc.setFillColor(...TEAL);
  doc.rect(120, y - 5, RM - 120, 9, 'F');
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...WHITE);
  doc.text('TOTAL DUE', 122, y + 1);
  doc.text(fmtMoney(invoice.total), 188, y + 1, { align: 'right' });

  // ── Payment instructions block ────────────────────────────────────────
  y += 18;
  if (y > 250) { doc.addPage(); y = 25; }
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...GRAY30);
  doc.text('Payment', LM, y);
  y += 5;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRAY60);
  doc.text('Net 14 — payment due 14 days from issue date.', LM, y);
  y += 5;
  doc.text('Pay via Stripe link sent in the invoice email, or contact billing@athomeblooddraw.com for ACH / wire details.', LM, y);
  if (invoice.notes) {
    y += 10;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...GRAY30);
    doc.text('Notes', LM, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...GRAY60);
    const noteLines = doc.splitTextToSize(invoice.notes, RM - LM);
    doc.text(noteLines, LM, y);
  }

  drawFooter(doc, 'AtHomeBloodDraw.com  ·  billing@athomeblooddraw.com');
  return doc.output('blob');
}

// ── PHLEB PAYSLIP ───────────────────────────────────────────────────────────

export function renderPhlebPayslipPdf({ payslip, lines, phleb }) {
  const doc = new jsPDF();
  const weekLabel = `Week ending ${fmtDate(payslip.week_ending)}`;
  drawHeader(doc, 'Payslip', weekLabel);

  // ── Pay-to + meta ──────────────────────────────────────────────────────
  let y = 45;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRAY100);
  doc.text('PAY TO', LM, y);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...GRAY30);
  doc.text(phleb?.full_name || 'Unknown phleb', LM, y + 6);
  if (phleb?.company_name) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(phleb.company_name, LM, y + 12);
  }
  if (phleb?.email) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...GRAY60);
    doc.text(phleb.email, LM, y + 18);
  }

  let mY = y;
  mY = metaPair(doc, 130, mY, 'PAYSLIP',     payslip.payslip_number || shortNumber('PAY', payslip.id));
  mY = metaPair(doc, 130, mY, 'ISSUE DATE',  fmtDate(payslip.generated_at || new Date()));
  mY = metaPair(doc, 130, mY, 'DRAWS',       String(payslip.draws_count || lines.filter((l) => !l.is_adjustment).length));

  // ── Line items header ──────────────────────────────────────────────────
  y = Math.max(y + 28, mY + 6);
  doc.setFillColor(...TEAL);
  doc.rect(LM, y, RM - LM, 7, 'F');
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...WHITE);
  doc.text('DATE',    LM + 2,  y + 5);
  doc.text('PATIENT', LM + 18, y + 5);
  doc.text('LAB',     LM + 72, y + 5);
  doc.text('RATE',    165,     y + 5, { align: 'right' });
  doc.text('AMOUNT',  188,     y + 5, { align: 'right' });
  // Header band is 7mm tall; advance enough for the 9pt body font ascender
  // (~3.2mm) plus a 3mm gap so the first row doesn't overlap the band edge.
  y += 13;

  // ── Line items + adjustments ──────────────────────────────────────────
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRAY30);
  lines.forEach((l, i) => {
    if (y > 255) {
      doc.addPage();
      y = 25;
    }
    if (i % 2 === 1) {
      doc.setFillColor(...ZEBRA);
      doc.rect(LM, y - 4, RM - LM, 6.5, 'F');
    }
    doc.setFontSize(9);
    if (l.is_adjustment) {
      doc.setFont('helvetica', 'italic');
      doc.text('—',                                              LM + 2,  y);
      doc.text(('Adjustment: ' + (l.adjustment_reason || '')).slice(0, 32), LM + 18, y);
      doc.text('—',                                              LM + 72, y);
      doc.text('—',                                              165,     y, { align: 'right' });
      const amt = Number(l.amount || 0);
      doc.setTextColor(amt < 0 ? 200 : 30, amt < 0 ? 50 : 30, 30);
      doc.text(fmtMoney(amt),                                    188,     y, { align: 'right' });
      doc.setTextColor(...GRAY30);
      doc.setFont('helvetica', 'normal');
    } else {
      doc.text(fmtShort(l.draw_date),                     LM + 2,  y);
      doc.text((l.patient_name || '—').slice(0, 32),      LM + 18, y);
      doc.text((l.lab_name || '—').slice(0, 26),          LM + 72, y);
      doc.text(fmtMoney(l.pay_rate),                      165,     y, { align: 'right' });
      doc.text(fmtMoney(l.amount),                        188,     y, { align: 'right' });
    }
    y += 6.5;
  });

  // ── Totals ─────────────────────────────────────────────────────────────
  y += 4;
  if (y > 250) { doc.addPage(); y = 25; }
  doc.setDrawColor(...GRAY220);
  doc.setLineWidth(0.3);
  doc.line(120, y, RM, y);
  y += 6;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRAY60);
  doc.text('Gross pay', 122, y);
  doc.text(fmtMoney(payslip.gross_pay), 188, y, { align: 'right' });
  if (Number(payslip.adjustments_total || 0) !== 0) {
    y += 6;
    doc.text('Adjustments', 122, y);
    doc.text(fmtMoney(payslip.adjustments_total), 188, y, { align: 'right' });
  }
  y += 8;
  doc.setFillColor(...TEAL);
  doc.rect(120, y - 5, RM - 120, 9, 'F');
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...WHITE);
  doc.text('NET PAY', 122, y + 1);
  doc.text(fmtMoney(payslip.net_pay), 188, y + 1, { align: 'right' });

  // ── Payment block ────────────────────────────────────────────────────
  y += 18;
  if (y > 250) { doc.addPage(); y = 25; }
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...GRAY30);
  doc.text('Payment', LM, y);
  y += 5;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRAY60);
  doc.text(
    'Paid via ACH to the bank account on file from your onboarding (W-9). Expect funds within 2 business days of the issue date.',
    LM, y
  );
  if (payslip.notes) {
    y += 10;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...GRAY30);
    doc.text('Notes', LM, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...GRAY60);
    const noteLines = doc.splitTextToSize(payslip.notes, RM - LM);
    doc.text(noteLines, LM, y);
  }

  drawFooter(doc, 'AtHomeBloodDraw.com  ·  Questions? payroll@athomeblooddraw.com');
  return doc.output('blob');
}
